import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRestaurantContext } from '@/contexts/RestaurantContext';
import { toast } from 'sonner';
import { getIsOnline } from '@/lib/connectivity';
import {
  getPendingOrders,
  updatePendingOrderStatus,
  deletePendingOrder,
  getPendingOrderCount,
  getPendingClosures,
  type PendingOrder,
} from '@/lib/offline-storage';
import { deductStockFromOrder } from '@/lib/stock-deduction';
import { printOrderByLocations, printCustomerReceipt } from '@/lib/order-printing';
import { logSyncDiscard } from '@/lib/sync-lock';

const SYNC_INTERVAL = 30000; // 30 seconds
const MAX_SYNC_ATTEMPTS = 5;

/**
 * Aguarda a sessão do Supabase estar disponível antes de prosseguir com o sync.
 * Evita 403 quando o GoTrueClient ainda está em _recoverAndRefresh após reconexão.
 */
async function waitForSession(maxWaitMs = 10000): Promise<boolean> {
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session?.access_token) return true;
    } catch (_e) {}
    await new Promise(r => setTimeout(r, 500));
  }
  return false;
}

export function useOfflineSync() {
  const { activeRestaurant } = useRestaurantContext();
  const [isOnline, setIsOnline] = useState(getIsOnline());
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isSyncingRef = useRef(false);

  // Update online status
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      toast.success('Conexão restaurada!', {
        description: 'Sincronizando pedidos pendentes...',
      });
      // waitForSession agora é executado dentro de syncPendingOrders (cobre todos os caminhos)
      syncPendingOrders();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning('Você está offline', {
        description: 'Os pedidos serão salvos localmente e sincronizados quando a conexão voltar.',
        duration: 5000,
      });
    };

    window.addEventListener('app:online', handleOnline);
    window.addEventListener('app:offline', handleOffline);

    return () => {
      window.removeEventListener('app:online', handleOnline);
      window.removeEventListener('app:offline', handleOffline);
    };
  }, []);

  // Update pending count
  const updatePendingCount = useCallback(async () => {
    if (!activeRestaurant) return;
    try {
      const count = await getPendingOrderCount(activeRestaurant.id);
      setPendingCount(count);
    } catch (error) {
      console.error('Error getting pending count:', error);
    }
  }, [activeRestaurant]);

  // Sync a single order
  const syncOrder = async (pendingOrder: PendingOrder): Promise<boolean> => {
    try {
      await updatePendingOrderStatus(pendingOrder.id, 'syncing');

      // === Replay via RPC resolve-or-create (send_order_items_fast) — mesmo do caminho online. ===
      // ANTES: um INSERT direto criava um pedido NOVO por pending → 2 itens offline na MESMA mesa
      // viravam 2 pedidos separados na reconexão. O RPC resolve o pedido ATIVO da mesa/comanda e
      // ANEXA os itens (o 1º pending cria; os seguintes anexam ao mesmo pedido). Os pendings já vêm
      // ordenados por createdAt ASC (getPendingOrders) e o loop é sequencial (for...of).
      // Idempotência: o RPC é ATÔMICO (rollback total em falha) e o pending só é removido APÓS o
      // sucesso → um re-replay (após falha no RPC) reaproveita/cria sem duplicar.
      const rpcItems = pendingOrder.items.map((item) => ({
        product_id: item.productId,
        product_name: item.productName,
        quantity: item.quantity,
        unit_price: item.unitPrice,
        total_price: item.unitPrice * item.quantity,
        notes: item.notes || null,
        combo_id: null,
        combo_selections: [],
        selected_addons: [],
      }));

      const { data: rpcResult, error: rpcError } = await supabase.rpc('send_order_items_fast', {
        p_restaurant_id: pendingOrder.restaurantId,
        p_table_id: pendingOrder.tableId || null,
        p_command_id: pendingOrder.commandId || null,
        p_order_type: pendingOrder.orderInfo.type,
        p_table_number: pendingOrder.orderInfo.tableNumber || null,
        p_tab_number: pendingOrder.orderInfo.tabNumber || null,
        p_customer_name: pendingOrder.orderInfo.customerName || null,
        p_waiter_name: null,
        p_notes: pendingOrder.orderInfo.notes || null,
        p_items: rpcItems,
        p_people_count: null,
        p_active_rodizio_combo_id: null,
        p_is_internal: true,
      } as any);

      if (rpcError) throw rpcError;
      const synced = rpcResult as { order_id: string; order_number: number; is_new_order: boolean } | null;
      if (!synced?.order_id) throw new Error('send_order_items_fast não retornou order_id');

      // Sucesso (RPC atômico) → remove o pending JÁ: é o ponto de commit que evita o re-replay
      // reanexar os mesmos itens. Estoque/impressão abaixo são best-effort e não re-duplicam o pedido.
      await deletePendingOrder(pendingOrder.id);

      // Baixa de estoque (best-effort).
      try {
        const stockItems = pendingOrder.items.map((item) => ({
          product: { id: item.productId, price: item.unitPrice } as any,
          quantity: item.quantity,
        }));
        await deductStockFromOrder(pendingOrder.restaurantId, synced.order_id, stockItems);
      } catch (stockError) {
        console.error('[OfflineSync] Baixa de estoque falhou (não-crítico):', stockError);
      }

      // Impressão (best-effort) — re-issue na reconexão (o ticket já foi impresso localmente offline).
      try {
        const rid = pendingOrder.restaurantId || activeRestaurant?.id;
        if (!rid) {
          console.warn('[OfflineSync] restaurantId indisponível — pulando impressão do recibo');
        } else {
          await printOrderByLocations(
            synced.order_number,
            rpcItems.map((item) => ({
              product_id: item.product_id || '',
              product_name: item.product_name,
              quantity: item.quantity,
              notes: item.notes || undefined,
            })),
            pendingOrder.orderInfo,
            rid
          );

          await printCustomerReceipt({
            orderNumber: synced.order_number,
            items: rpcItems.map((item) => ({
              name: item.product_name,
              quantity: item.quantity,
              unit_price: item.unit_price,
              total_price: item.total_price,
            })),
            subtotal: pendingOrder.subtotal,
            serviceTax: pendingOrder.serviceTax,
            discount: pendingOrder.discount,
            total: pendingOrder.total,
            paymentMethod: pendingOrder.payments[0]?.paymentMethodName || 'Não informado',
            customerName: pendingOrder.orderInfo.customerName,
            restaurantName: activeRestaurant?.name || '',
          });
        }
      } catch (printError) {
        console.error('[OfflineSync] Impressão falhou (não-crítico):', printError);
      }

      return true;
    } catch (error) {
      console.error('Error syncing order:', error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      await updatePendingOrderStatus(pendingOrder.id, 'failed', errorMessage);
      
      return false;
    }
  };

  // Sync all pending orders
  const syncPendingOrders = useCallback(async () => {
    if (!activeRestaurant || !isOnline || isSyncingRef.current) return;

    try {
      isSyncingRef.current = true;
      setIsSyncing(true);

      // Aguardar sessão Supabase válida antes de qualquer insert (evita 403 por auth.uid() null).
      // Cobre todos os caminhos: handleOnline, initial sync e interval periódico.
      console.log('[OfflineSync] Waiting for session before sync...');
      const ready = await waitForSession(10000);
      if (!ready) {
        console.warn('[OfflineSync] Session not ready after 10s, aborting');
        return;
      }
      console.log('[OfflineSync] Session ready, starting sync');

      const pendingOrders = await getPendingOrders(activeRestaurant.id);
      
      if (pendingOrders.length === 0) {
        return;
      }

      // Load pending closures to detect tables that were closed after orders were created
      const pendingClosures = await getPendingClosures(activeRestaurant.id);

      let syncedCount = 0;
      let failedCount = 0;
      let skippedCount = 0;

      for (const order of pendingOrders) {
        // Check if this table was closed AFTER this order was created (same logic as useOfflineSyncExtended)
        if (order.tableId) {
          const wasClosedLater = pendingClosures.some(c => 
            c.tableId === order.tableId && 
            new Date(c.createdAt) > new Date(order.createdAt)
          );
          
          if (wasClosedLater) {
            console.log(`[OfflineSync] Skipping order for table ${order.tableId} - was closed later`);
            await deletePendingOrder(order.id);
            skippedCount++;
            continue;
          }
        }

        // Past max attempts: leave for manual review, do NOT auto-delete.
        // A table being "available" on the server is the NORMAL precondition
        // for creating an offline order — it means the offline order hasn't
        // synced yet, not that it's orphaned.
        if (order.syncAttempts >= MAX_SYNC_ATTEMPTS) {
          failedCount++;
          // Instrumentation: log permanent failure once when MAX hit.
          if (order.syncAttempts === MAX_SYNC_ATTEMPTS) {
            logSyncDiscard({
              restaurantId: activeRestaurant.id,
              reason: 'max_sync_attempts_pending_order',
              operation: 'INSERT',
              table: 'orders',
              recordId: order.id,
              attempts: order.syncAttempts,
              errorMessage: order.lastSyncError,
              payload: { orderInfo: order.orderInfo, total: order.total },
            });
          }
          continue;
        }

        // Always attempt to sync. Mesa "available" no servidor = pré-condição
        // válida para criar o pedido offline. Mesa "occupied" = syncOrder vai
        // criar um novo pedido (não temos como bater IDs com o servidor pois
        // o pending_order foi gerado offline).
        if (order.tableId) {
          console.log(`[OfflineSync] Syncing offline order for table ${order.tableId}`);
        }

        const success = await syncOrder(order);
        if (success) {
          syncedCount++;
        } else {
          failedCount++;
        }
      }

      if (syncedCount > 0) {
        toast.success(`${syncedCount} pedido(s) sincronizado(s)!`);
      }

      if (failedCount > 0) {
        toast.error(`${failedCount} pedido(s) falharam na sincronização`, {
          description: 'Tente novamente ou verifique os pedidos pendentes.',
        });
      }

      await updatePendingCount();
    } catch (error) {
      console.error('Error syncing pending orders:', error);
    } finally {
      setIsSyncing(false);
      isSyncingRef.current = false;
    }
  }, [activeRestaurant, isOnline, updatePendingCount]);

  // Start sync interval when online
  useEffect(() => {
    if (isOnline && activeRestaurant) {
      // Initial sync
      syncPendingOrders();
      updatePendingCount();

      // Set up interval
      syncIntervalRef.current = setInterval(() => {
        syncPendingOrders();
        updatePendingCount();
      }, SYNC_INTERVAL);
    }

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
        syncIntervalRef.current = null;
      }
    };
  }, [isOnline, activeRestaurant, syncPendingOrders, updatePendingCount]);

  // Manual sync trigger
  const triggerSync = useCallback(() => {
    if (isOnline) {
      syncPendingOrders();
    } else {
      toast.error('Sem conexão', {
        description: 'Conecte-se à internet para sincronizar os pedidos.',
      });
    }
  }, [isOnline, syncPendingOrders]);

  return {
    isOnline,
    isSyncing,
    pendingCount,
    triggerSync,
    updatePendingCount,
  };
}
